import { createSlice } from "@reduxjs/toolkit";
import { collection, getDocs, query, limit, startAfter, where, doc, getDoc, orderBy } from "firebase/firestore";
import { firestore } from "../firebase/firebase";
import { getAuth } from "firebase/auth";

const DEFAULT_PAGE_SIZE = 5;

// Fetch Initial Data Function
export const fetchAppData = (fetchConfigs = {}) => async (dispatch, getState) => {
    dispatch(setLoading(true));
    try {
        const collections = [
            "users", "stories", "activity", "contests", "following",
            "forum-questions", "forum-answers", "forum-comments", "potds", "news", "user-prompts"
        ];
        
        const dataFetches = collections.map(async (col) => {
            const config = fetchConfigs[col] || {};
            const pageSize = config.pageSize || DEFAULT_PAGE_SIZE;
            let q = query(collection(firestore, col), limit(pageSize));
            
            if (config.filters) {
                config.filters.forEach(filter => {
                    q = query(q, where(filter.field, filter.op, filter.value));
                });
            }
            
            const snapshot = await getDocs(q);
            return {
                key: col,
                data: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1].id : null
            };
        });


        //STORIES

         // Fetch 10 most recent stories
         const recentQuery = query(
            collection(firestore, "stories"),
            orderBy("time", "desc"),
            limit(1)
        );
        const recentSnap = await getDocs(recentQuery);
        const recentStories = recentSnap.docs.map(doc => ({ id: doc.uid, ...doc.data() }));

        // Fetch 10 most popular stories
        const popularQuery = query(
            collection(firestore, "stories"),
            orderBy("influence", "desc"),
            limit(1)
        );
        const popularSnap = await getDocs(popularQuery);
        const popularStories = popularSnap.docs.map(doc => ({ id: doc.uid, ...doc.data() }));

        // Fetch 10 random stories
        const randomSeed = Math.random();
        const randomQuery = query(
            collection(firestore, "stories"),
            where("randomKey", ">=", randomSeed),
            limit(1)
          );          
        const randomSnap = await getDocs(randomQuery);

        if (randomSnap.size < 1) {
        const query2 = query(
            collection(firestore, "stories"),
            where("randomKey", "<", randomSeed),
            limit(1 - randomSnap.size)
        );
        const randomSnap2 = await getDocs(query2);

        const stories = [...randomSnap.docs, ...randomSnap2.docs].map(doc => doc.data());
        return stories;
        }

        const randomStories = randomSnap.docs.map(doc => doc.data());
        // Combine all stories and remove duplicates by ID
        const combinedStories = [...recentStories, ...popularStories, ...randomStories];

        const allStories = Array.from(
            new Map(combinedStories.map(story => [story.uid, story])).values()
          );

        dispatch(setStories({ data: allStories, lastDoc: recentSnap.docs.length > 0 ? recentSnap.docs[recentSnap.docs.length - 1].id : null, replace: true }));


        //USERS
        // Fetch 10 users with the most points
        const topUsersQuery = query(
            collection(firestore, "users"),
            orderBy("points", "desc"),
            limit(10)
        );
        const topUsersSnap = await getDocs(topUsersQuery);
        const topUsers = topUsersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        dispatch(setUsers({ data: topUsers, lastDoc: null, replace: true }));


        const results = await Promise.all(dataFetches);
        
        const actionMap = {
            "activity": setActivity,
            "contests": setContests,
            "following": setFollowing,
            "forum-questions": setForumQuestions,
            "forum-answers": setForumAnswers,
            "forum-comments": setForumComments,
            "potds": setPotds,
            "news": setNews,
            "user-prompts": setUserPrompts
        };

        results.forEach(({ key, data, lastDoc }) => {
            const action = actionMap[key];
            if (action) {
                dispatch(action({ data, lastDoc, replace: true }));
            }
        });



        const { currentUser } = getAuth();
        if (currentUser) {
            let userData = results.find(r => r.key === "users").data.find(user => user.username === currentUser.displayName);
            
            if (!userData) {
                const userRef = doc(firestore, "users", currentUser.displayName);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                    userData = { id: userSnap.id, ...userSnap.data() };

                    // Check if the user is already in the users state
                    const existingUsers = getState().app.users;
                    if (!existingUsers.some(user => user.id === userData.id)) {
                        // Add user to the state if not already present
                        dispatch(setUsers({ data: [userData], lastDoc: null, replace: false }));
                    }
                }
            }
            
            // Set the user in the user state
            if (userData) {
                dispatch(setUser(userData));
            }
        }


    } catch (error) {
        console.error("Failed to fetch app data:", error);
        dispatch(setError(error.message || "Failed to fetch app data."));
    } finally {
        dispatch(setLoading(false));
    }
};

// Fetch More Data Function for each collection
// Enhanced Fetch More Data Function for any Firestore collection
export const createFetchMoreData = (collectionName, setCollectionAction) =>
    (fetchConfig = {}) =>
      async (dispatch, getState) => {
        const lastDocId = getState().app[`${collectionName}LastDoc`];
        console.log('👉 lastDocId:', lastDocId);
        if (!lastDocId) return;
  
        try {
          const lastDocRef = doc(firestore, collectionName, lastDocId);
          const lastDocSnap = await getDoc(lastDocRef);
          if (!lastDocSnap.exists()) {
            console.warn('❌ lastDocSnap does not exist');
            return;
          }
  
          const pageSize = fetchConfig.pageSize || DEFAULT_PAGE_SIZE;
          const constraints = [];
  
          if (fetchConfig.filters) {
            fetchConfig.filters.forEach((filter) => {
              console.log('🔍 Adding filter:', filter);
              constraints.push(where(filter.field, filter.op, filter.value));
            });
          }
  
          if (fetchConfig.orderBy) {
            console.log('📏 Using orderBy:', fetchConfig.orderBy);
            const { field, direction } = fetchConfig.orderBy;
            constraints.push(orderBy(field, direction));
          }
  
          constraints.push(startAfter(lastDocSnap));
          constraints.push(limit(pageSize));
  
          const q = query(collection(firestore, collectionName), ...constraints);
          console.log('📡 Running query with constraints:', constraints);
  
          const snapshot = await getDocs(q);
          const newData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          console.log(`📦 Fetched ${newData.length} docs`);
  
          const newLastDoc =
            snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1].id : null;
  
          dispatch(
            setCollectionAction({ data: newData, lastDoc: newLastDoc, replace: false })
          );
  
          return newData;
        } catch (error) {
          console.error(`🔥 Failed to fetch more ${collectionName}:`, error);
        }
      };
  
      const deduplicateById = (existing, incoming) => {
        const existingIds = new Set(existing.map(item => item.uid));
        return [...existing, ...incoming.filter(item => !existingIds.has(item.uid))];
    };
    
  

// Redux Slice
export const appSlice = createSlice({
    name: 'app',
    initialState: {
        users: [], stories: [], activity: [], contests: [], following: [],
        forumQuestions: [], forumAnswers: [], forumComments: [], potds: [], news: [], userPrompts: [],
        usersLastDoc: null, storiesLastDoc: null, activityLastDoc: null, contestsLastDoc: null, followingLastDoc: null,
        forumQuestionsLastDoc: null, forumAnswersLastDoc: null, forumCommentsLastDoc: null, potdsLastDoc: null, newsLastDoc: null, userPromptsLastDoc: null,
        user: null, isLoading: false, error: null
    },
    reducers: {
        setUsers: (state, action) => {
            state.users = action.payload.replace
                ? action.payload.data
                : deduplicateById(state.users, action.payload.data);
            state.usersLastDoc = action.payload.lastDoc;
        },
        setStories: (state, action) => {
            state.stories = action.payload.replace
                ? action.payload.data
                : deduplicateById(state.stories, action.payload.data);
                if (action.payload.lastDoc) {
                    state.storiesLastDoc = action.payload.lastDoc;
                }
                console.log('SET_STORIES payload:', JSON.stringify(action.payload.lastDoc), new Date().toISOString());
            },       
        setActivity: (state, action) => {
            state.activity = action.payload.replace
                ? action.payload.data
                : deduplicateById(state.activity, action.payload.data);
            state.activityLastDoc = action.payload.lastDoc;
        },
        setContests: (state, action) => {
            state.contests = action.payload.replace
                ? action.payload.data
                : deduplicateById(state.contests, action.payload.data);
            state.contestsLastDoc = action.payload.lastDoc;
        },
        setFollowing: (state, action) => {
            state.following = action.payload.replace
                ? action.payload.data
                : deduplicateById(state.following, action.payload.data);
            state.followingLastDoc = action.payload.lastDoc;
        },
        setUser: (state, action) => {
            state.user = action.payload;
        },
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        }
    }
});

export const { setUsers, setStories, setActivity, setContests, setFollowing, setForumQuestions, setForumAnswers, setForumComments, setPotds, setNews, setUserPrompts, setUser, setLoading, setError } = appSlice.actions;

export default appSlice.reducer;
